import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import { UsernameInput } from './UsernameInput';
import { PasskeysManager } from './PasskeysManager';
import { toast } from 'react-hot-toast';
import { 
  User, 
  Key, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Sparkles
} from 'lucide-react';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  required: boolean;
}

export const OnboardingFlow: React.FC = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [username, setUsername] = useState('');
  const [usernameValid, setUsernameValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Wishful!',
      description: 'Let\'s set up your account to get you started.',
      icon: Sparkles,
      required: false
    },
    {
      id: 'username',
      title: 'Choose Your Username',
      description: 'This will be your unique identifier for sharing wishlists.',
      icon: User,
      required: true
    },
    {
      id: 'passkeys',
      title: 'Secure Your Account (Optional)',
      description: 'Add a passkey for passwordless, secure authentication.',
      icon: Key,
      required: false
    },
    {
      id: 'complete',
      title: 'You\'re All Set!',
      description: 'Your account is ready. Start creating your first wishlist!',
      icon: CheckCircle,
      required: false
    }
  ];

  useEffect(() => {
    // Check if user already has a username
    if (user) {
      checkExistingProfile();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkExistingProfile = async () => {
    if (!user) return;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (profile?.username) {
        // User already has username, skip onboarding
        window.location.href = '/';
        return;
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const handleUsernameChange = async (value: string) => {
    setUsername(value);
    
    try {
      // Import the validator
      const { UsernameValidator } = await import('../utils/usernameValidator');
      
      // Validate format
      const validation = UsernameValidator.validate(value);
      if (!validation.isValid) {
        setUsernameValid(false);
        return;
      }

      // Check availability
      const availability = await UsernameValidator.checkAvailability(value, supabase, user?.id);
      setUsernameValid(availability.available);
    } catch (error) {
      console.error('Error validating username:', error);
      setUsernameValid(false);
    }
  };

  const saveUsername = async () => {
    if (!user || !usernameValid) return;

    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          username: username.toLowerCase(),
          email: user.email,
          display_name: user.user_metadata?.full_name || '',
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      toast.success('Username saved successfully!');
      setCurrentStep(prev => prev + 1);
    } catch (error: any) {
      console.error('Error saving username:', error);
      toast.error('Failed to save username. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    const currentStepData = steps[currentStep];
    
    if (currentStepData.id === 'username') {
      if (usernameValid) {
        saveUsername();
      } else {
        toast.error('Please choose a valid username');
      }
    } else if (currentStepData.id === 'passkeys') {
      // Skip passkeys step
      setCurrentStep(prev => prev + 1);
    } else if (currentStepData.id === 'complete') {
      // Complete onboarding
      window.location.href = '/';
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleSkip = () => {
    if (steps[currentStep].id === 'passkeys') {
      setCurrentStep(prev => prev + 1);
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];
    const Icon = step.icon;

    switch (step.id) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <Icon className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h2>
              <p className="text-gray-600">{step.description}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">What you'll get:</h3>
              <ul className="text-gray-700 text-sm space-y-1">
                <li>• Save products from any store</li>
                <li>• Share wishlists with friends</li>
                <li>• Secure passwordless login</li>
                <li>• Beautiful, modern interface</li>
              </ul>
            </div>
          </div>
        );

      case 'username':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <Icon className="w-8 h-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h2>
              <p className="text-gray-600">{step.description}</p>
            </div>
            
            <div className="max-w-md mx-auto">
              <UsernameInput
                value={username}
                onChange={handleUsernameChange}
                userId={user?.id}
                disabled={isSaving}
              />
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">Why usernames matter:</h3>
              <ul className="text-green-800 text-sm space-y-1">
                <li>• Share wishlists with @{username || 'username'}</li>
                <li>• Keep your email private</li>
                <li>• Professional, clean URLs</li>
                <li>• Easy to remember and type</li>
              </ul>
            </div>
          </div>
        );

      case 'passkeys':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <Icon className="w-8 h-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h2>
              <p className="text-gray-600">{step.description}</p>
            </div>
            
            {user && (
              <div className="max-w-2xl mx-auto">
                <PasskeysManager userId={user.id} />
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-medium text-amber-900 mb-2">Passkeys are optional but recommended:</h3>
              <ul className="text-amber-800 text-sm space-y-1">
                <li>• More secure than passwords</li>
                <li>• No passwords to remember</li>
                <li>• Works with Face ID, Touch ID, Windows Hello</li>
                <li>• Resistant to phishing attacks</li>
              </ul>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Icon className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h2>
              <p className="text-gray-600">{step.description}</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">What's next?</h3>
              <ul className="text-green-800 text-sm space-y-1">
                <li>• Create your first wishlist</li>
                <li>• Add products by pasting URLs</li>
                <li>• Share with friends and family</li>
                <li>• Get notified when prices drop</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="flex items-center space-x-3">
            {steps[currentStep].id === 'passkeys' && (
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Skip
              </button>
            )}
            
            <button
              onClick={handleNext}
              disabled={
                (steps[currentStep].id === 'username' && (!usernameValid || isSaving)) ||
                isSaving
              }
              className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>
                {steps[currentStep].id === 'complete' ? 'Get Started' : 'Next'}
              </span>
              {steps[currentStep].id !== 'complete' && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
